<template>
  <div v-if="manageableCampaigns.length" class="avocash-ledger-section">
    <div class="avocash-section-header display-flex justify-content-space-between align-items-center mb-3">
      <h3 class="font-headline-sm font-bold text-on-surface m-0">{{ t('dashboardTx.title') || 'Journal des Transactions' }}</h3>
      <span class="font-label-sm text-outline" v-if="rows.length">{{ rows.length }} opérations</span>
    </div>

    <!-- Filter & Campaign Selector Card -->
    <f7-card class="avocash-filter-card" :padding="false">
      <f7-card-content class="avocash-filter-card-content" :padding="false">
        <div class="grid grid-cols-2 gap-2">
          <div class="avocash-select-group display-flex flex-direction-column">
            <label class="font-label-sm text-outline mb-1">{{ t('dashboardTx.campaign') }}</label>
            <select v-model="selectedId" class="avocash-select-input">
              <option v-for="c in manageableCampaigns" :key="c.id" :value="c.id">
                {{ c.name || c.title || c.id }}
              </option>
            </select>
          </div>

          <div class="avocash-select-group display-flex flex-direction-column">
            <label class="font-label-sm text-outline mb-1">{{ t('dashboardTx.status') }}</label>
            <select v-model="statusFilter" class="avocash-select-input">
              <option value="">{{ t('dashboardTx.statusAll') }}</option>
              <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
        </div>
      </f7-card-content>
    </f7-card>

    <!-- Time Period Selector Pills -->
    <div class="avocash-period-pills display-flex gap-2 overflow-x-auto pb-1 mb-3" role="tablist">
      <f7-button
        v-for="p in presets"
        :key="p.value"
        class="avocash-pill-btn"
        :fill="preset === p.value"
        :outline="preset !== p.value"
        :rounded="true"
        size="small"
        @click="setPreset(p.value)"
        role="tab"
        :aria-selected="preset === p.value"
      >
        {{ p.label }}
      </f7-button>
    </div>

    <!-- Custom Date Range -->
    <f7-card v-if="preset === 'custom'" class="avocash-custom-range" :padding="false">
      <f7-card-content class="avocash-custom-range-content" :padding="false">
        <div class="grid grid-cols-2 gap-2">
          <div class="avocash-date-input display-flex flex-direction-column">
            <label class="font-label-sm text-outline mb-1">{{ t('dashboardTx.from') }}</label>
            <input type="date" v-model="customFrom" @change="reload" class="avocash-select-input" />
          </div>
          <div class="avocash-date-input display-flex flex-direction-column">
            <label class="font-label-sm text-outline mb-1">{{ t('dashboardTx.to') }}</label>
            <input type="date" v-model="customTo" @change="reload" class="avocash-select-input" />
          </div>
        </div>
      </f7-card-content>
    </f7-card>

    <!-- Financial Summary Cards -->
    <div class="avocash-summary-grid grid grid-cols-3 gap-2 mb-3">
      <f7-card class="avocash-summary-card" :padding="false">
        <f7-card-content class="avocash-summary-card-content" :padding="false">
          <span class="font-label-sm text-outline d-block">{{ t('dashboardTx.txCount') }}</span>
          <span class="font-headline-sm font-bold text-on-surface d-block mt-1">{{ summary.totalCount ?? '-' }}</span>
        </f7-card-content>
      </f7-card>
      <f7-card class="avocash-summary-card primary" :padding="false">
        <f7-card-content class="avocash-summary-card-content" :padding="false">
          <span class="font-label-sm text-primary-fixed d-block">{{ t('dashboardTx.confirmedAmount') }}</span>
          <span class="font-headline-sm font-bold text-white d-block mt-1">{{ fmtMoney(summary.confirmedAmount, primaryCurrency) }}</span>
        </f7-card-content>
      </f7-card>
      <f7-card class="avocash-summary-card" :padding="false">
        <f7-card-content class="avocash-summary-card-content" :padding="false">
          <span class="font-label-sm text-outline d-block">{{ t('dashboardTx.totalAmount') }}</span>
          <span class="font-headline-sm font-bold text-on-surface d-block mt-1">{{ fmtMoney(summary.totalAmount, primaryCurrency) }}</span>
        </f7-card-content>
      </f7-card>
    </div>

    <!-- Export Actions -->
    <div class="avocash-export-bar display-flex gap-2 mb-3 flex-wrap">
      <f7-button
        class="avocash-export-btn"
        fill
        outline
        size="small"
        :disabled="exporting || !rows.length"
        @click="doExport('csv')"
      >
        <f7-icon material="table_chart" :size="16" /> {{ t('dashboardTx.exportCsv') }}
      </f7-button>
      <f7-button
        class="avocash-export-btn"
        fill
        size="small"
        :disabled="exporting || !rows.length"
        @click="doExport('xlsx')"
      >
        <f7-icon material="download" :size="16" /> {{ t('dashboardTx.exportXlsx') }}
      </f7-button>
      <f7-preloader v-if="exporting" size="18" />
    </div>

    <!-- Loading & Empty States -->
    <SkeletonList v-if="loadingRows && !rows.length" variant="row" :rows="4" />

    <div v-else-if="!rows.length" class="avocash-state-pad text-align-center text-outline">
      {{ t('dashboardTx.none') }}
    </div>

    <!-- Transaction Rows -->
    <f7-list v-else class="avocash-tx-list" inset strong>
      <f7-list-item
        v-for="r in rows"
        :key="r.reference || r.id"
        class="avocash-tx-item"
      >
        <template #media>
          <div class="avocash-tx-avatar" :class="'status-' + chipColor(r.status)">
            <f7-icon
              :material="chipColor(r.status) === 'green' ? 'payments' : 'hourglass_top'"
              :size="20"
            />
          </div>
        </template>
        <div class="avocash-tx-info flex-1 min-w-0">
          <h4 class="font-body-md font-semibold text-on-surface m-0 mb-1 truncate">{{ r.description || r.referenceNote || r.reference }}</h4>
          <span class="font-body-sm text-outline d-block">{{ r.payerName || '' }}{{ r.payerName ? ' • ' : '' }}{{ fmtDate(r.createdAt) }}</span>
          <div class="mt-1">
            <f7-badge :class="'c-' + chipColor(r.status)" class="avocash-status-pill">
              {{ chipLabel(r.status) }}
            </f7-badge>
          </div>
        </div>
        <template #after>
          <span class="avocash-tx-amount font-headline-sm font-bold" :class="{ 'text-primary': chipColor(r.status) === 'green', 'text-on-surface': chipColor(r.status) !== 'green' }">
            {{ fmtMoney(r.amount, r.currency) }}
          </span>
        </template>
      </f7-list-item>
    </f7-list>

    <!-- Load More -->
    <div v-if="canLoadMore" class="avocash-more-wrap text-align-center mt-3">
      <f7-button
        class="avocash-more-btn"
        fill
        outline
        rounded
        size="small"
        :disabled="loadingRows"
        @click="loadMore"
      >
        {{ t('dashboardTx.more') }}
      </f7-button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { f7 } from 'framework7-vue';
import i18n from '@/js/i18n.js';
import SkeletonList from '@/components/common/SkeletonList.vue';
import {
  getEntityTransactions,
  getEntityTransactionSummary,
} from '@/js/api/payments.js';
import { useCampaignsStore } from '@/js/pinia-stores/campaign.js';
import { useKeycloakStore } from '@/js/pinia-stores/keycloak.js';
import { useTransactionStatus } from '@/js/composables/useTransactionStatus.js';
import { formatMoney } from '@/js/utils/money.js';
import { formatDateTime } from '@/js/utils/datetime.js';
import { exportRows } from '@/js/utils/exportFile.js';

const props = defineProps({
  groupId: { type: String, required: true },
});

const { t } = i18n.global;
const { transactionStatus } = useTransactionStatus();

const campaignsStore = useCampaignsStore();
const keycloakStore = useKeycloakStore();

const PAGE_SIZE = 20;
const EXPORT_MAX = 500;

const manageableCampaigns = computed(() => {
  const uid = keycloakStore.subject;
  if (!uid) return [];
  return (campaignsStore.campaigns || []).filter((c) => {
    const owner = c.createdBy && String(c.createdBy) === String(uid);
    const managers = Array.isArray(c.campaignManagers)
      ? c.campaignManagers.map(String)
      : [];
    return owner || managers.includes(String(uid));
  });
});

const presets = [
  { value: '7d', label: t('dashboardTx.last7') },
  { value: '30d', label: t('dashboardTx.last30') },
  { value: 'all', label: t('dashboardTx.allTime') },
  { value: 'custom', label: t('dashboardTx.custom') },
];

const selectedId = ref('');
const preset = ref('30d');
const customFrom = ref('');
const customTo = ref('');
const statusFilter = ref('');

const statusOptions = [
  'SUCCESS', 'PENDING', 'PROCESSING', 'FAILED', 'EXPIRED',
  'REFUNDED', 'PARTIALLY_REFUNDED', 'CANCELLED',
];

const rows = ref([]);
const page = ref(0);
const totalPages = ref(1);
const loadingRows = ref(false);
const exporting = ref(false);
const summary = ref({});
const loaded = ref(false);

const canLoadMore = computed(() => page.value + 1 < (totalPages.value ?? 1));

const primaryCurrency = computed(() => {
  return summary.value.currencies?.[0] || 'EUR';
});

function rangeBounds() {
  const now = new Date();
  const iso = (d) => d.toISOString();
  if (preset.value === '7d' || preset.value === '30d') {
    const days = preset.value === '7d' ? 7 : 30;
    return { from: iso(new Date(now.getTime() - days * 86400000)), to: null };
  }
  if (preset.value === 'all') return { from: null, to: null };
  return {
    from: customFrom.value ? new Date(`${customFrom.value}T00:00:00`).toISOString() : null,
    to: customTo.value ? new Date(`${customTo.value}T23:59:59`).toISOString() : null,
  };
}

async function fetchSummary() {
  if (!selectedId.value) return;
  try {
    const { from, to } = rangeBounds();
    const data = await getEntityTransactionSummary('CAMPAIGN', selectedId.value, { from, to });
    const bs = data.byStatus || {};
    const confirmed =
      (bs.SUCCESS?.amount || 0) +
      (bs.CONFIRMED?.amount || 0) +
      (bs.COMPLETE?.amount || 0);
    const confirmedCount =
      (bs.SUCCESS?.count || 0) + (bs.CONFIRMED?.count || 0) + (bs.COMPLETE?.count || 0);
    summary.value = {
      totalCount: data.totalCount,
      totalAmount: Number(data.totalAmount || 0),
      confirmedAmount: Number(confirmed),
      confirmedCount,
      currencies: data.currencies || [],
    };
  } catch (err) {
    console.warn('[dashboardTx] summary failed', err);
  }
}

async function fetchPage(nextPage) {
  if (!selectedId.value) return;
  loadingRows.value = true;
  try {
    const { from, to } = rangeBounds();
    const data = await getEntityTransactions(
      'CAMPAIGN',
      selectedId.value,
      { from, to, status: statusFilter.value ? [statusFilter.value] : [] },
      { page: nextPage, size: PAGE_SIZE }
    );
    totalPages.value = data.totalPages ?? 1;
    rows.value = nextPage === 0 ? (data.content || []) : rows.value.concat(data.content || []);
    page.value = nextPage;
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || '';
    if (!String(msg).includes('403')) {
      console.warn('[dashboardTx] list failed', err);
    }
  } finally {
    loadingRows.value = false;
  }
}

function reload() {
  if (!selectedId.value) return;
  summary.value = {};
  rows.value = [];
  totalPages.value = 1;
  fetchSummary();
  fetchPage(0);
}

function loadMore() {
  fetchPage(page.value + 1);
}

function setPreset(key) {
  preset.value = key;
  if (key !== 'custom') reload();
}

watch(selectedId, () => reload());
watch(statusFilter, () => reload());

onMounted(async () => {
  try {
    await campaignsStore.fetchByGroup(props.groupId);
  } catch (err) {
    console.warn('[dashboardTx] campaigns load failed', err);
    return;
  }
  if (manageableCampaigns.value.length) {
    selectedId.value = manageableCampaigns.value[0].id;
    loaded.value = true;
  }
});

function slug(s) {
  return String(s || '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 32);
}

async function doExport(format) {
  if (!selectedId.value || exporting.value) return;
  exporting.value = true;
  try {
    const { from, to } = rangeBounds();
    const all = [];
    let p = 0;
    let pages = 1;
    do {
      // eslint-disable-next-line no-await-in-loop
      const data = await getEntityTransactions(
        'CAMPAIGN',
        selectedId.value,
        { from, to, status: statusFilter.value ? [statusFilter.value] : [] },
        { page: p, size: 100 }
      );
      pages = data.totalPages ?? 1;
      all.push(...(data.content || []));
      p += 1;
    } while (p < pages && all.length < EXPORT_MAX);

    const columns = [
      { key: 'reference', label: t('txnResult.reference') },
      { key: 'createdAt', label: t('dashboardTx.colDate') },
      { key: 'amount', label: t('dashboardTx.colAmount') },
      { key: 'currency', label: t('dashboardTx.colCurrency') },
      { key: 'status', label: t('dashboardTx.colStatus') },
      { key: 'paymentPartner', label: t('dashboardTx.colProvider') },
      { key: 'payerName', label: t('dashboardTx.colPayer') },
      { key: 'description', label: t('dashboardTx.colDescription') },
    ];
    const prepared = all.slice(0, EXPORT_MAX).map((r) => ({
      ...r,
      createdAt: r.createdAt ? new Date(r.createdAt).toISOString().slice(0, 19).replace('T', ' ') : '',
    }));
    const bounds = rangeBounds();
    const stamp = `${bounds.from ? bounds.from.slice(0, 10) : 'start'}_${bounds.to ? bounds.to.slice(0, 10) : 'now'}`;
    const name = selectedCampaignName();
    await exportRows({
      rows: prepared,
      columns,
      format,
      filename: `avocash-${slug(name)}-${stamp}.${format}`,
    });
    f7.toast.show({ text: t('dashboardTx.exported'), closeTimeout: 1500 });
  } catch (err) {
    console.warn('[dashboardTx] export failed', err);
    f7.toast.show({ text: t('dashboardTx.exportFailed'), closeTimeout: 2500 });
  } finally {
    exporting.value = false;
  }
}

function selectedCampaignName() {
  const c = (campaignsStore.campaigns || []).find((x) => x.id === selectedId.value);
  return c ? c.name || c.title || c.id : 'campaign';
}

function chipLabel(status) {
  return transactionStatus(status)?.status || status || '-';
}
function chipColor(status) {
  return transactionStatus(status)?.color || 'gray';
}
function fmtMoney(v, currency) {
  if (v == null) return '-';
  return formatMoney(v, currency);
}
function fmtDate(d) {
  return d ? formatDateTime(d) : '';
}
</script>

<style scoped>
.avocash-ledger-section {
  margin-top: 20px;
}
.avocash-filter-card {
  margin-bottom: 12px;
}
.avocash-filter-card-content {
  padding: 12px 14px;
}
.avocash-select-group {
  display: flex;
  flex-direction: column;
}
.avocash-select-input {
  font-family: var(--avocash-font-body, 'Work Sans', sans-serif);
  font-size: 13px;
  font-weight: 600;
  color: #161d18;
  background: #edf6ed;
  border: none;
  border-radius: 8px;
  padding: 6px 8px;
  outline: none;
}
.avocash-period-pills {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin-bottom: 12px;
  padding-bottom: 4px;
}
.avocash-custom-range {
  margin-bottom: 12px;
}
.avocash-custom-range-content {
  padding: 12px 14px;
}
.avocash-date-input {
  display: flex;
  flex-direction: column;
}
.avocash-summary-grid {
  margin-bottom: 14px;
}
.avocash-summary-card {
  border-radius: 12px;
}
.avocash-summary-card.primary {
  background: #1A6B42;
}
.avocash-summary-card-content {
  padding: 10px 8px;
  text-align: center;
}
.avocash-export-bar {
  margin-bottom: 14px;
}
.avocash-tx-list {
  border-radius: 16px;
  overflow: hidden;
}
.avocash-tx-item {
  padding: 14px;
}
.avocash-tx-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.status-green { background: #a5f4bf; color: #00512e; }
.status-orange, .status-darkorange { background: #ffe08f; color: #785d00; }
.status-red { background: #ffdadb; color: #772e37; }
.status-gray { background: #e8f0e7; color: #6f7a71; }
.avocash-tx-info {
  flex: 1;
  min-width: 0;
}
.avocash-tx-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.avocash-tx-meta {
  font-size: 11px;
  display: block;
}
.avocash-status-pill {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
}
.c-green { background: #a5f4bf; color: #002110; }
.c-orange, .c-darkorange { background: #ffe08f; color: #241a00; }
.c-red { background: #ffdadb; color: #3e030f; }
.c-gray { background: #e8f0e7; color: #3f4941; }
.avocash-tx-amount {
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 14px;
  font-weight: 700;
}
.avocash-tx-amount.text-primary {
  color: #1A6B42;
}
.avocash-more-wrap {
  margin-top: 12px;
}
.avocash-state-pad {
  padding: 24px;
  text-align: center;
}
</style>

